import { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Switch } from 'wouter';
import type { User } from 'firebase/auth';
import Layout from './components/Layout';
import SignInGate from './components/SignInGate';
import RouteFallback from './components/RouteFallback';
// Every page is its own chunk: the shell (auth, layout, nav) is what loads
// first, and a panel's code only downloads when someone opens it.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Settings = lazy(() => import('./pages/Settings'));
const AdsDashboard = lazy(() => import('./pages/AdsDashboard'));
const Creative = lazy(() => import('./pages/Creative'));
const CreativePipeline = lazy(() => import('./pages/CreativePipeline'));
const CreativeStoryboard = lazy(() => import('./pages/CreativeStoryboard'));
const CreativeLocalize = lazy(() => import('./pages/CreativeLocalize'));
const CellDetail = lazy(() => import('./pages/CellDetail'));
const Playbooks = lazy(() => import('./pages/Playbooks'));
const ConceptDetail = lazy(() => import('./pages/ConceptDetail'));
const Lifecycle = lazy(() => import('./pages/Lifecycle'));
const FullFunnel = lazy(() => import('./pages/FullFunnel'));
const Creators = lazy(() => import('./pages/Creators'));
const Affiliates = lazy(() => import('./pages/Affiliates'));
const CreatorDetail = lazy(() => import('./pages/CreatorDetail'));
const Financial = lazy(() => import('./pages/Financial'));
const Reddit = lazy(() => import('./pages/Reddit'));
const Testing = lazy(() => import('./pages/Testing'));
const Journal = lazy(() => import('./pages/Journal'));
const LandingTests = lazy(() => import('./pages/LandingTests'));
const ScalingBoard = lazy(() => import('./pages/ScalingBoard'));
const Press = lazy(() => import('./pages/Press'));
const Venues = lazy(() => import('./pages/Venues'));
import { watchAuth } from './lib/firebase';
import { useRole } from './lib/useRole';

// Role gate for the ported Outclose panels: /ads admits admin+analytics,
// /creative admits admin+creative. The server enforces the same rules; this
// just avoids rendering a page that will only 403.
function RequireRole({
  allowed,
  children,
}: {
  allowed: (r: ReturnType<typeof useRole>) => boolean;
  children: React.ReactNode;
}) {
  const role = useRole();
  if (role.loading) {
    return <RouteFallback />;
  }
  if (!allowed(role)) {
    return (
      <div className="text-center py-20 text-neutral-500">
        Your account doesn&apos;t have access to this panel.
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    return watchAuth((u) => {
      setUser(u);
      setAuthReady(true);
    });
  }, []);

  // Same loader as the boot splash in index.html, in phase with it, so the
  // handover from static HTML to React is invisible.
  if (!authReady) {
    return <RouteFallback full />;
  }

  if (!user) {
    return <SignInGate />;
  }

  return (
    <Layout user={user}>
      <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/campaigns" component={Campaigns} />
        <Route path="/ads">
          <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
            <AdsDashboard />
          </RequireRole>
        </Route>
        <Route path="/landing">
          <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
            <LandingTests />
          </RequireRole>
        </Route>
        {/* /launches is the pre-rename alias — same page. */}
        <Route path="/journal">
          <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
            <Journal />
          </RequireRole>
        </Route>
        {['/testing', '/launches'].map((p) => (
          <Route key={p} path={p}>
            <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
              <Testing />
            </RequireRole>
          </Route>
        ))}
        <Route path="/creative/localize">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <CreativeLocalize />
          </RequireRole>
        </Route>
        <Route path="/creative/playbooks">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <Playbooks />
          </RequireRole>
        </Route>
        <Route path="/creative/cells/:id">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <CellDetail />
          </RequireRole>
        </Route>
        <Route path="/creative/concepts/:id">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <ConceptDetail />
          </RequireRole>
        </Route>
        <Route path="/creative/pipeline">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <CreativePipeline />
          </RequireRole>
        </Route>
        <Route path="/creative/storyboard">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <CreativeStoryboard />
          </RequireRole>
        </Route>
        <Route path="/creative">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <Creative />
          </RequireRole>
        </Route>
        <Route path="/scaling">
          <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
            <ScalingBoard />
          </RequireRole>
        </Route>
        <Route path="/funnel">
          <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
            <FullFunnel />
          </RequireRole>
        </Route>
        <Route path="/lifecycle">
          <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
            <Lifecycle />
          </RequireRole>
        </Route>
        <Route path="/reddit">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <Reddit />
          </RequireRole>
        </Route>
        <Route path="/creators/:id">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <CreatorDetail />
          </RequireRole>
        </Route>
        <Route path="/creators">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <Creators />
          </RequireRole>
        </Route>
        <Route path="/affiliates">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <Affiliates />
          </RequireRole>
        </Route>
        <Route path="/press">
          <RequireRole allowed={(r) => r.hasCreativeAccess}>
            <Press />
          </RequireRole>
        </Route>
        <Route path="/venues">
          <RequireRole allowed={(r) => r.isAdmin}>
            <Venues />
          </RequireRole>
        </Route>
        <Route path="/financial">
          <RequireRole allowed={(r) => r.hasAnalyticsAccess}>
            <Financial />
          </RequireRole>
        </Route>
        <Route path="/settings" component={Settings} />
        <Route>
          <div className="text-center py-20 text-neutral-500">Page not found</div>
        </Route>
      </Switch>
      </Suspense>
    </Layout>
  );
}
